import { useRef, useCallback } from 'react';
import { ALLOWED_ATTACHMENT_TYPES, MAX_ATTACHMENT_SIZE } from '@/api/types';
import { showError } from '@/lib/toast';

interface FileInputProps {
  onSelect: (file: File) => void;
  disabled?: boolean;
}

/**
 * Hidden file input with button trigger for attachment upload
 *
 * Features:
 * - Client-side validation for file type and size
 * - Shows alert for invalid files
 * - Hidden input with visible button trigger
 */
export function FileInput({ onSelect, disabled }: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input value to allow selecting the same file again
      e.target.value = '';

      // Validate file type
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type as typeof ALLOWED_ATTACHMENT_TYPES[number])) {
        showError(
          'File type not allowed. Please upload an image, PDF, Word, Excel, CSV, or text file.'
        );
        return;
      }

      // Validate file size
      if (file.size > MAX_ATTACHMENT_SIZE) {
        const maxSizeMB = MAX_ATTACHMENT_SIZE / (1024 * 1024);
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        showError(`File is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`);
        return;
      }

      onSelect(file);
    },
    [onSelect]
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
        onChange={handleChange}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Attach file"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
          />
        </svg>
      </button>
    </>
  );
}
